import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@material-ui/core';

import SupportRow from 'components/Onboarding/SupportRow';
import FloatingLanguageSwitcher from 'components/FloatingLanguageSwitcher';
import { keaSignIn } from 'actions/Auth';
import { bridgeRegister, bridgeGetDetails } from 'slices/BridgeSlice';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const REGISTER_TIMEOUT_MS = 60_000;

const WelcomePage = () => {
  const localizeMessage = useLocalizedMessage();
  const dispatch = useDispatch();
  const authState = useSelector(({ auth }) => auth);
  const details = useSelector(({ bridge }) => bridge.details);
  const bridgeErrorNonce = useSelector(({ bridge }) => bridge.errorNonce);

  const [formType, setFormType] = useState('register');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isRegistering, setIsRegistering] = useState(false);

  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  useEffect(() => { dispatch(bridgeGetDetails()); }, [dispatch]);

  const handleSwitcher = (type) => {
    setFormType(type);
    setErrors({});
  };

  const handleLoginClick = () => {
    dispatch(keaSignIn({
      details,
      email: emailInputRef.current.value,
      password: passwordInputRef.current.value,
      mode: 'embedded',
      flow: 'login_plugin',
    }));
  };

  const validateRegister = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = localizeMessage('embedded.validation.nameRequired');
    if (!email.trim()) newErrors.email = localizeMessage('embedded.validation.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = localizeMessage('embedded.validation.emailInvalid');
    if (!password) newErrors.password = localizeMessage('embedded.validation.passwordRequired');
    else if (password.length < 8) newErrors.password = localizeMessage('embedded.validation.passwordTooShort');
    return newErrors;
  };

  const handleRegisterClick = () => {
    if (isRegistering) return;

    const validationErrors = validateRegister();
    if (Object.keys(validationErrors).length === 0) {
      setIsRegistering(true);
      dispatch(bridgeRegister({ name, email, password }));
    } else {
      setErrors(validationErrors);
    }
  };

  // Immediately re-enable on bridge error/timeout (BRIDGE_ERROR)
  useEffect(() => {
    if (!isRegistering) return;
    setIsRegistering(false);
  }, [bridgeErrorNonce]);

  // Fallback: if nothing happened (no reload, no error), re-enable after timeout
  useEffect(() => {
    if (!isRegistering) return;
    const t = setTimeout(() => setIsRegistering(false), REGISTER_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [isRegistering]);

  const sliderLeft = formType === 'login' ? '0%' : '50%';
  const formsTransform = formType === 'login' ? 'translateX(0%)' : 'translateX(-50%)';

  const existingAccountForm = (
    <Box className="form-pane">
      {authState.showMessage && (
        <div className="loading-spinner">
          <CircularProgress />
        </div>
      )}
      <Typography style={{ marginBottom: 16 }} variant="body2" color="textSecondary">
        {localizeMessage('embedded.signin.title')}
      </Typography>
      <TextField
        label={localizeMessage('embedded.businessEmail')}
        variant="outlined"
        fullWidth
        required
        inputRef={emailInputRef}
        className="form-field"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
      />
      <TextField
        label={localizeMessage('embedded.password')}
        variant="outlined"
        type="password"
        fullWidth
        required
        inputRef={passwordInputRef}
        className="form-field"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!errors.password}
        helperText={errors.password}
      />
      <Button variant="contained" fullWidth color="primary" onClick={handleLoginClick}>
        {localizeMessage('embedded.signIn')}
      </Button>
      <Box textAlign="center">
        <Link to="/embedded/forgot-password" className="forgot-link">
          {localizeMessage('embedded.forgotPassword')}
        </Link>
      </Box>
    </Box>
  );

  const newUserForm = (
    <Box className="form-pane">
      <Typography style={{ marginBottom: 16 }} variant="body2" color="textSecondary">
        {localizeMessage('embedded.register.title')}
      </Typography>
      <TextField
        label={localizeMessage('embedded.name')}
        variant="outlined"
        fullWidth
        required
        className="form-field"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={!!errors.name}
        helperText={errors.name}
        disabled={isRegistering}
      />
      <TextField
        label={localizeMessage('embedded.businessEmail')}
        variant="outlined"
        fullWidth
        required
        className="form-field"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        disabled={isRegistering}
      />
      <TextField
        label={localizeMessage('embedded.password')}
        variant="outlined"
        type="password"
        fullWidth
        required
        className="form-field"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!errors.password}
        helperText={errors.password}
        disabled={isRegistering}
      />
      <Button
        variant="contained"
        fullWidth
        color="primary"
        onClick={handleRegisterClick}
        disabled={isRegistering}
      >
        {isRegistering ? (
          <>
            <CircularProgress size={20} style={{ marginRight: 8 }} />
            {localizeMessage('embedded.registering')}
          </>
        ) : localizeMessage('embedded.register')}
      </Button>
    </Box>
  );

  return (
    <div className="welcome-page container">
      <FloatingLanguageSwitcher />
      <div className="row justify-content-center text-center mt-5 mb-4">
        <div className="col-md-12">
          <img
            src={require('assets/images/kealogo.svg')}
            alt={localizeMessage('brand.logoAlt')}
            style={{ maxHeight: '52px' }}
          />
          <Typography variant="h4" className="main-header">{localizeMessage('embedded.welcome.title')}</Typography>
          <Typography variant="h5" className="sub-header">
            {localizeMessage('embedded.welcome.subtitle')}
          </Typography>
        </div>
      </div>

      <div className="row justify-content-center mb-4">
        <div className="mt-5 mb-4 col-5">
          <div className="switcher-wrapper">
            <div className="slider-bg" style={{ left: sliderLeft }} />
            <button
              className={classNames('switcher-button', { 'switcher-button-active': formType === 'login' })}
              onClick={() => handleSwitcher('login')}
              type="button"
              disabled={isRegistering}
            >
              {localizeMessage('embedded.existingAccount')}
            </button>
            <button
              className={classNames('switcher-button', { 'switcher-button-active': formType === 'register' })}
              onClick={() => handleSwitcher('register')}
              type="button"
              disabled={isRegistering}
            >
              {localizeMessage('embedded.createAccount')}
            </button>
          </div>
          <div className="forms-outer">
            <div className="forms-inner" style={{ transform: formsTransform }}>
              {existingAccountForm}
              {newUserForm}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <SupportRow />
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
